import { CircularProgress, Stack } from '@mui/material';

import { ChatCitationSideContentHost } from '@/modules/common/Chat/ChatSideContent/ChatCitationSideContentHost';
import { ChatShell } from '@/modules/common/Chat/components/ChatShell';
import { AnnotatedPDFViewerProvider } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.provider';

import { useHouseholdChatContext } from '../contexts/householdChat.context';
import { HouseholdChatComposer } from './HouseholdChatComposer';
import { HouseholdChatConversationList } from './HouseholdChatConversationList';
import { HouseholdChatEmptyState } from './HouseholdChatEmptyState';
import { HouseholdChatHeader } from './HouseholdChatHeader';
import { HouseholdChatTranscript } from './HouseholdChatTranscript';

/**
 * The chat column content, agnostic of how it's hosted: the chat header on
 * top, the transcript (or conversation list) below it, and the composer at
 * the bottom. The docked and overlay hosts wrap it in their chrome, size it,
 * and render the side content panel beside it.
 */
export function HouseholdChatShell() {
  const { isValidationQuestionsConversation, messages, messagesLoading, view } =
    useHouseholdChatContext();

  const isListView = view === 'list';
  // The list view has no composer, and validation-questions conversations are
  // answered through their question cards rather than a free-form message.
  const showComposer = !isListView && !isValidationQuestionsConversation;

  // While a selected conversation's history loads (and nothing is shown yet),
  // surface a spinner via the empty-state slot instead of the prompt copy.
  const emptyState =
    messagesLoading && messages.length === 0 ? (
      <Stack sx={{ alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Stack>
    ) : (
      <HouseholdChatEmptyState />
    );

  return (
    // Citations rendered in the transcript (CitationPopper) resolve their
    // evidence into this provider; the citation host watches it and opens the
    // cited document (in the side content panel when available, otherwise in
    // the modal). documentId is null because chat isn't scoped to one
    // document — the clicked citation supplies the target. The panel renders
    // outside this provider: its content mounts its own panel-scoped one.
    <AnnotatedPDFViewerProvider
      documentId={null}
      showCitations
      showCitationCoachmark={false}
    >
      <ChatShell
        emptyState={emptyState}
        footer={showComposer ? <HouseholdChatComposer /> : undefined}
        // The list view reuses the transcript slot for the conversation list;
        // hasMessages only picks between the transcript and empty-state slots.
        hasMessages={isListView || messages.length > 0}
        header={<HouseholdChatHeader />}
        pinTranscriptToBottom={!isListView}
        transcript={
          isListView ? (
            <HouseholdChatConversationList />
          ) : (
            <HouseholdChatTranscript />
          )
        }
      />
      <ChatCitationSideContentHost />
    </AnnotatedPDFViewerProvider>
  );
}
